<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import OnboardingStepContainer from './OnboardingStepContainer.svelte';
	import {
		Alert,
		Checkbox,
		Icon,
		Input,
		Label,
		Popover,
		Text,
	} from '@Shelly/shelly-design-system/index';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import {
		calculateWidths,
		convertDistUnits,
		conversionFactors,
		repositionChartWidthsLabels,
		DISTANCE_MIN_NOTATION,
		DISTANCE_BASE_SIZE,
	} from 'src/helpers/distance_helper';
		import { real_id } from 'src/helpers/interface_helpers';
	import LiveDistanceComponent from './LiveDistanceComponent.svelte';

	$: settingsStore = getDeviceSettings(device.id);
	export let device;
	export let deviceData;

	$: distanceUnit = device?.ht_ui?.unit || $settingsStore?.ht_ui?.unit || 'mm';

	const dispatch = createEventDispatcher();

	let error;
	let errorMin;
	let errorMax;
	let errorGarage;
	let displayMeterRange;
	let displayMeterRangeStart;
	let displayMeterRangeEnd;
	let displayClosedPositionRange;
	let displayClosedToleranceRange;
	const totalMax = 200;

	$: topLabel = deviceData?.reverseMeasurement ? '0%' : '100%';
	$: bottomLabel = deviceData?.reverseMeasurement ? '100%' : '0%';
	$: step = distanceUnit === 'in' ? '0.01' : '1';
	$: factor = conversionFactors[distanceUnit] || 1;

	$: installationHeight =
		deviceData.modeType === 'occupancy'
			? $format('chart_baseline_distance', {
					default: 'Baseline{br}distance',
					values: { br: '<br/>' },
				})
			: $format('chart_installation_height', {
					default: 'Installation{br}height',
					values: { br: '<br/>' },
				});

	$: meterRange =
		deviceData.modeType === 'occupancy'
			? $format('chart_trigger_distance', {
					default: 'Trigger{br}distance',
					values: { br: '<br/>' },
				})
			: $format('chart_meter_range', {
					default: 'Meter{br}range',
					values: { br: '<br/>' },
				});

	$: closedPositionDistance = $format('chart_closed_distance', {
		default: 'Closed{br}position{br}distance',
		values: { br: '<br/>' },
	});

	$: closedDoorShutter = $format('chart_closed_position', {
		default: 'Closed{br}door/shutter',
		values: { br: '<br/>' },
	});

	$: garageTypeValue =
		typeof deviceData.garageType === 'string' ? deviceData.garageType : deviceData.garageType?.value;

	$: floorWall =
		garageTypeValue === 'Vertical'
			? $format('chart_label_floor', { default: 'Floor' })
			: $format('chart_label_wall', { default: 'Wall' });

	$: chartWidths = calculateWidths(
		Number(deviceData.meterRangeStart),
		Number(deviceData.meterRangeEnd),
		Number(totalMax),
		Number(deviceData.installationHeight),
	);

	$: {
		error = valueOutOfRange(
			+convertValue(+deviceData.meterRange),
			distanceUnit === 'in' ? 7.88 : 200,
			+convertValue(+deviceData.installationHeight),
		)
			? $format('min_max_error_description', {
					values: {
						min: distanceUnit === 'in' ? '7.88' : '200',
						max: convertValue(+deviceData.installationHeight),
					},
					default: 'Value must be between {min} and {max}.',
				})
			: undefined;

		errorMin = valueOutOfRange(
			+convertValue(+deviceData.meterRangeStart),
			+convertValue(+deviceData.meterRangeEnd),
			+convertValue(+deviceData.installationHeight),
		)
			? $format('min_max_error_description', {
					values: {
						min: convertValue(+deviceData.meterRangeEnd) || 0,
						max: convertValue(+deviceData.installationHeight),
					},
					default: 'Value must be between {min} and {max}.',
				})
			: undefined;

		errorMax = valueOutOfRange(
			+convertValue(+deviceData.meterRangeEnd),
			distanceUnit === 'in' ? 7.88 : 200,
			+convertValue(+deviceData.meterRangeStart),
		)
			? $format('min_max_error_description', {
					values: {
						min: distanceUnit === 'in' ? '7.88' : '200',
						max: +convertValue(+deviceData.meterRangeStart) || 0,
					},
					default: 'Value must be between {min} and {max}.',
				})
			: undefined;

		errorGarage = valueOutOfRange(
			+convertValue(+deviceData.closedPositionRange),
			distanceUnit === 'in' ? 7.88 : 200,
			distanceUnit === 'in' ? 196.85 : 5000,
		)
			? $format('min_max_error_description', {
					values: {
						min: distanceUnit === 'in' ? '7.88' : '200',
						max: distanceUnit === 'in' ? '196.85' : '5000',
					},
					default: 'Value must be between {min} and {max}.',
				})
			: undefined;

		dispatch('error', {
			error,
			errorMin,
			errorMax,
			errorGarage,
		});
	}

	$: {
		// Convert the canonical value (in mm) to the display unit.
		const calculatedMeterRange = deviceData.meterRange / factor;
		const calculatedMeterRangeStart = deviceData.meterRangeStart / factor;
		const calculatedMeterRangeEnd = deviceData.meterRangeEnd / factor;
		const calculatedClosedPositionRange = deviceData.closedPositionRange / factor;
		const calculatedClosedToleranceRange = deviceData.closedToleranceRange / factor;

		// This conditions are crucial. It prevents overwriting user input by only
		// updating the display if it's out of sync with the underlying data.
		if (
			displayMeterRange === undefined ||
			Math.abs(displayMeterRange - calculatedMeterRange) > DISTANCE_MIN_NOTATION
		) {
			displayMeterRange = calculatedMeterRange.toFixed(distanceUnit === 'in' ? 2 : 0);
		}

		if (
			displayMeterRangeStart === undefined ||
			Math.abs(displayMeterRangeStart - calculatedMeterRangeStart) > DISTANCE_MIN_NOTATION
		) {
			displayMeterRangeStart = calculatedMeterRangeStart.toFixed(distanceUnit === 'in' ? 2 : 0);
		}

		if (
			displayMeterRangeEnd === undefined ||
			Math.abs(displayMeterRangeEnd - calculatedMeterRangeEnd) > DISTANCE_MIN_NOTATION
		) {
			displayMeterRangeEnd = calculatedMeterRangeEnd.toFixed(distanceUnit === 'in' ? 2 : 0);
		}

		if (
			displayClosedPositionRange === undefined ||
			Math.abs(displayClosedPositionRange - calculatedClosedPositionRange) > DISTANCE_MIN_NOTATION
		) {
			displayClosedPositionRange = calculatedClosedPositionRange.toFixed(
				distanceUnit === 'in' ? 2 : 0,
			);
		}

		if (
			displayClosedToleranceRange === undefined ||
			Math.abs(displayClosedToleranceRange - calculatedClosedToleranceRange) >
				DISTANCE_MIN_NOTATION
		) {
			displayClosedToleranceRange = calculatedClosedToleranceRange.toFixed(
				distanceUnit === 'in' ? 2 : 0,
			);
		}
	}

	let subTitle: string;
	let leftInputText: string;

	$: if (deviceData.modeType === 'garage') {
		subTitle = $format('text_garage_subtitle', {
			default: 'Move the door or shutter to its fully closed position.',
		});
		leftInputText = $format('label_closed_pos_distance', {
			default: 'Closed position distance ({unit})',
			values: {
				unit: convertDistUnits(distanceUnit),
			},
		});
	} else if (deviceData.modeType === 'occupancy') {
		subTitle = $format('text_occupancy_bounds', {
			default:
				"Capture the distance when the parking spot is empty. We'll use this baseline to decide when the spot becomes Occupied vs Vacant.",
		});
		leftInputText = $format('label_trigger_distance', {
			default: 'Trigger distance ({unit})',
			values: {
				unit: convertDistUnits(distanceUnit),
			},
		});
	} else {
		subTitle = $format('text_liquid_bounds_new', {
			default: 'Set your empty ({empty}) and full ({full}) points so % reads accurately.',
			values: deviceData?.reverseMeasurement
				? { empty: '100%', full: '0%' }
				: { empty: '0%', full: '100%' },
		});
		leftInputText = $format('label_meter_bounds', {
			default: 'Meter bounds ({unit})',
			values: {
				unit: convertDistUnits(distanceUnit),
			},
		});
	}

	const convertValue = (value: number, unit?: string) => {
		if ((unit && unit === 'in') || distanceUnit === 'in') {
			return (value / factor).toFixed(2);
		}
		return value.toFixed(0);
	};

	const handleInput = (event: Event, deviceKey: string, displayKey: string) => {
		const target = event.target as HTMLInputElement;
		const newDisplayValue = target.value;

		// Update the corresponding display variable immediately for responsiveness
		switch (displayKey) {
			case 'displayMeterRange':
				displayMeterRange = newDisplayValue;
				break;
			case 'displayMeterRangeStart':
				displayMeterRangeStart = newDisplayValue;
				break;
			case 'displayMeterRangeEnd':
				displayMeterRangeEnd = newDisplayValue;
				break;
			case 'displayClosedPositionRange':
				displayClosedPositionRange = newDisplayValue;
				break;
			case 'displayClosedToleranceRange':
				displayClosedToleranceRange = newDisplayValue;
				break;
		}

		// Update the data model if the input is a valid number
		const numericValue = Number(newDisplayValue);
		if (!isNaN(numericValue)) {
			deviceData[deviceKey] = numericValue * factor;
		}
	};
</script>

<OnboardingStepContainer>
	<svelte:fragment slot="text">
		<Text styleType="body-small" class="mb-m">{subTitle}</Text>
	</svelte:fragment>

	<svelte:fragment slot="left">
		{#if deviceData.modeType === 'garage'}
			<Text styleType="body-small" class="mb-m">
				{$format('text_garage_set_distance', {
					default:
						'Set the distance from the sensor to the reflective surface (>{value} {unit} is recommended).',
					values: {
						value: distanceUnit === 'in' ? '11.81' : '300',
						unit: convertDistUnits(distanceUnit),
					},
				})}
			</Text>
		{/if}
		<div class="mb-s d-flex align-items-center">
			<Text styleType="body-small" class="text-600 mr-s">{leftInputText}</Text>
			<Popover type="hover" hideCloseBtn placement="top">
				<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
				<div slot="customContent" class="d-flex flex-direction-column">
					{#if deviceData.modeType === 'garage'}
						<Text styleType="body-extra-small">
							{$format('text_garage_in_use_distance', {
								default:
									'When the device is in use, distances close to this value are shown as Closed - any other distance is treated as Open.',
							})}
						</Text>
					{:else if deviceData.modeType === 'occupancy'}
						<Text styleType="body-extra-small"
							>{$format('popover_text_occupancy_live_distance', {
								default:
									'If the live distance becomes at least this much shorter than the baseline, the spot is Occupied.',
							})}</Text
						>
					{:else}
						<Text styleType="body-extra-small"
							>{$format('popover_text_liquid_distance', {
								default: 'This slider maps distance to the main UI meter:',
							})}</Text
						>
						<ul class="body-extra-small-styles">
							<li>
								{$format('popover_li_left_new', {
									default: 'Left = {empty} (empty) - farthest.',
									values: { empty: deviceData?.reverseMeasurement ? '100%' : '0%' },
								})}
							</li>
							<li>
								{$format('popover_li_right_new', {
									default: 'Right = {full} (full) - nearest.',
									values: { full: deviceData?.reverseMeasurement ? '0%' : '100%' },
								})}
							</li>
						</ul>
					{/if}
				</div>
			</Popover>
		</div>
		{#if deviceData.modeType === 'garage'}
			<Input
				id="input-garage-bounds"
				type="number"
				aria-labelledby="input-garage-bounds"
				icon="far fa-ruler-horizontal"
				size="m"
				label={distanceUnit === 'in' ? '11.81' : '300'}
				class="mb-s"
				value={displayClosedPositionRange}
				on:input={(e) => handleInput(e, 'closedPositionRange', 'displayClosedPositionRange')}
				min={distanceUnit === 'in' ? '7.88' : '200'}
				max={distanceUnit === 'in' ? '196.85' : '5000'}
				error={errorGarage}
				{step}
			/>
			<LiveDistanceComponent
				{device}
				{deviceData}
				{distanceUnit}
				on:click={(e) => (deviceData.closedPositionRange = e.detail.liveDistance)}
			/>
			<Alert
				type="info"
				class="mb-s"
				heading={$format('alert_capture_distance', {
					default:
						'Capture distance: Sets the closed position from the Live distance measurement.',
				})}
			/>
			<div class="mb-s d-flex align-items-center">
				<Text styleType="body-small" class="text-600 mr-s">
					{$format('label_tolerance_amount', {
						default: 'Tolerance amount ({unit})',
						values: {
							unit: convertDistUnits(distanceUnit),
						},
					})}
				</Text>
				<Popover type="hover" hideCloseBtn placement="top">
					<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
					<div slot="customContent" class="d-flex flex-direction-column">
						<Text styleType="body-extra-small">
							{$format('text_tolerance_helper', {
								default:
									'The maximum allowable deviation from the fully closed position of the garage/shutter door.',
							})}
						</Text>
					</div>
				</Popover>
			</div>
			<Input
				id="input-garage-tolerance"
				type="number"
				aria-labelledby="input-garage-tolerance"
				icon="far fa-arrows-from-line"
				size="m"
				label={distanceUnit === 'in' ? '0.39' : '10'}
				class="mb-s"
				value={displayClosedToleranceRange}
				on:input={(e) => handleInput(e, 'closedToleranceRange', 'displayClosedToleranceRange')}
				{step}
			/>
			<div class="d-flex align-items-center">
				<Checkbox
					size="m"
					class="mr-s"
					id="checkbox-ffk3jkasdq"
					bind:checked={deviceData.measureFromBase}
					>{$format('checkbox_measure_from_base', {
						default: 'Measure from device base',
					})}</Checkbox
				>
				<Popover type="hover" hideCloseBtn placement="top">
					<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
					<Text slot="customContent" styleType="body-extra-small">
						{$format('popover_text_measure_from_base', {
							default:
								'Adds a base offset (measure from mounting base, not sensor face). Default {value} {unit}.',
							values: {
								value:
									distanceUnit === 'in'
										? (DISTANCE_BASE_SIZE / factor).toFixed(2)
										: DISTANCE_BASE_SIZE,
								unit: convertDistUnits(distanceUnit),
							},
						})}
					</Text>
				</Popover>
			</div>
		{:else if deviceData.modeType === 'occupancy'}
			<Input
				id="input-distance-bounds"
				type="number"
				aria-labelledby="input-distance-bounds"
				icon="far fa-ruler-horizontal"
				size="m"
				label={distanceUnit === 'in' ? '39.37' : '1000'}
				class="mb-s"
				value={displayMeterRange}
				on:input={(e) => handleInput(e, 'meterRange', 'displayMeterRange')}
				min={distanceUnit === 'in' ? '7.88' : '200'}
				max={convertValue(+deviceData.installationHeight)}
				{error}
				{step}
			/>
			<Text styleType="body-extra-small" class="mb-s text-color-3">
				{$format('info_default_distance_bounds', {
					default: 'Default: {value} {unit}',
					values: {
						value: convertValue(1000, distanceUnit),
						unit: convertDistUnits(distanceUnit),
					},
				})}
			</Text>
			<Alert
				type="info"
				heading={$format('alert_info_distance_bounds', {
					default:
						'Keep {default} for most installs. Try {minLower} - {maxLower} for lower cars; {minHigher} - {maxHigher} for taller vehicles (SUVs/vans).',
					values: {
						default: `${convertValue(1000, distanceUnit)} ${convertDistUnits(distanceUnit)}`,
						minLower: convertValue(800, distanceUnit),
						maxLower: `${convertValue(1200, distanceUnit)} ${convertDistUnits(distanceUnit)}`,
						minHigher: convertValue(1200, distanceUnit),
						maxHigher: `${convertValue(1600, distanceUnit)} ${convertDistUnits(distanceUnit)}`,
					},
				})}
			/>
		{:else}
			<div class="mb-xs labels-container">
				<div
					class="slider-label"
					style="left: {repositionChartWidthsLabels(chartWidths.left - 7)}%;"
				>
					<Label
						value={`${convertValue(+deviceData.meterRangeStart) || 0} ${convertDistUnits(distanceUnit)}`}
						size="s"
					/>
				</div>
				<div
					class="slider-label"
					style="left: {repositionChartWidthsLabels(
						chartWidths.left + chartWidths.middle - 7,
					)}%;"
				>
					<Label
						value={`${convertValue(+deviceData.meterRangeEnd) || 0} ${convertDistUnits(distanceUnit)}`}
						size="s"
					/>
				</div>
			</div>
			<div class="mb-xs d-flex linear-chart">
				<div style="width: {chartWidths.left}%;" />
				<div style="width: {chartWidths.middle}%;" />
				<div style="width: {chartWidths.right}%;" />
			</div>
			<div class="mb-s d-flex justify-content-between">
				<Text styleType="body-extra-small" class="text-300"
					>{`${convertValue(+deviceData.installationHeight)} ${convertDistUnits(distanceUnit)}`}</Text
				>
				<Text styleType="body-extra-small" class="text-300"
					>{`${distanceUnit === 'in' ? '7.88' : '200'} ${convertDistUnits(distanceUnit)}`}
				</Text>
			</div>
			<Text styleType="body-extra-small" class="mb-m text-color-3">
				{$format('info_liquid_meter_bounds_new', {
					default: 'Left corresponds to {empty}. Right to {full}.',
					values: deviceData?.reverseMeasurement
						? { empty: '100%', full: '0%' }
						: { empty: '0%', full: '100%' },
				})}</Text
			>
			<Text styleType="body-small" class="mb-xs">
				{$format('zero_distance_bounds_new', {
					default: 'Empty distance - {unit} ({empty})',
					values: {
						unit: convertDistUnits(distanceUnit),
						empty: deviceData?.reverseMeasurement ? '100%' : '0%',
					},
				})}
			</Text>
			<Input
				id="min-input-distance-bounds"
				type="number"
				aria-labelledby="min-input-distance-bounds"
				icon="far fa-ruler-horizontal"
				size="m"
				label={distanceUnit === 'in' ? '64.96' : '1650'}
				class="mb-s"
				min={convertValue(+deviceData.meterRangeEnd)}
				max={convertValue(+deviceData.installationHeight)}
				value={displayMeterRangeStart}
				on:input={(e) => handleInput(e, 'meterRangeStart', 'displayMeterRangeStart')}
				error={errorMin}
				{step}
			/>
			<Text styleType="body-small" class="mb-xs">
				{$format('full_distance_bounds_new', {
					default: 'Full distance - {unit} ({full})',
					values: {
						unit: convertDistUnits(distanceUnit),
						full: deviceData?.reverseMeasurement ? '0%' : '100%',
					},
				})}
			</Text>
			<Input
				id="max-input-distance-bounds"
				type="number"
				aria-labelledby="max-input-distance-bounds"
				icon="far fa-ruler-horizontal"
				size="m"
				class="mb-s"
				label={distanceUnit === 'in' ? '33.46' : '850'}
				min={distanceUnit === 'in' ? '7.88' : '200'}
				max={convertValue(+deviceData.meterRangeStart)}
				value={displayMeterRangeEnd}
				on:input={(e) => handleInput(e, 'meterRangeEnd', 'displayMeterRangeEnd')}
				error={errorMax}
				{step}
			/>
			<Alert
				type="info"
				heading={$format('alert_info_hint_scene_automations', {
					default:
						'Use scene automations later to trigger actions based on sensor readings. For example, receive notifications or alerts, control other devices, or even trigger other scenes when distance thresholds are reached.',
				})}
			/>
			<div class="mt-s d-flex align-items-center">
				<Checkbox
					size="m"
					bind:checked={deviceData.reverseMeasurement}
					id="checkbox-6asd54asf"
					class="mr-s"
					>{$format('setting_reverse_measurement', {
						default: 'Reverse measurement direction',
					})}</Checkbox
				>
				<Popover type="hover" hideCloseBtn placement="top">
					<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
					<Text slot="customContent" styleType="body-extra-small">
						{$format('text_reverse_measurement', {
							default:
								'Use the reverse measurement option when the sensor is mounted upside down or in a way that the distance readings need to be inverted.',
						})}
					</Text>
				</Popover>
			</div>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="right">
		{#if deviceData.modeType === 'garage'}
			<div class="garage-diagram-container">
				<div class="garage-diagram">
					<img
						src="./images/onboarding/distance/calibration_vertical.png"
						alt={$format('calibration_garage_diagram', {
							default: 'Calibration garage diagram',
						})}
						width="360"
						height="266"
					/>
					<span class="garage-label text-center">{@html closedPositionDistance}</span>
					<span class="garage-label text-center text-nowrap">{@html closedDoorShutter}</span>
					<span class="garage-label text-center">{@html floorWall}</span>
				</div>
			</div>
		{:else if deviceData.modeType === 'occupancy'}
			<div class="occupancy-diagram-container">
				<div class="occupancy-diagram">
					<img
						src="./images/onboarding/distance/step3_trigger_distance.png"
						alt={$format('triggrer_distance_diagram', {
							default: 'Trigger distance diagram',
						})}
					/>
					<span class="occupancy-label text-center">{@html installationHeight}</span>
					<span class="occupancy-label text-center">{@html meterRange}</span>
				</div>
			</div>
		{:else}
			<div class="liquid-diagram-container">
				<div class="liquid-diagram">
					<img
						src="./images/onboarding/distance/step3_meter_bounds.png"
						alt={$format('meter_bounds_diagram', {
							default: 'Meter bounds diagram',
						})}
					/>
					<span class="liquid-label text-center">{@html installationHeight}</span>
					<span
						class={`liquid-label text-center ${deviceData?.reverseMeasurement ? 'reverse' : ''}`}
					>
						{topLabel}
					</span>
					<span
						class={`liquid-label text-center ${deviceData?.reverseMeasurement ? 'reverse' : ''}`}
					>
						{bottomLabel}
					</span>
					<span class="liquid-label text-center">{@html meterRange}</span>
				</div>
			</div>
		{/if}
	</svelte:fragment>
</OnboardingStepContainer>

<style>
	.labels-container {
		position: relative;
		overflow: hidden;
		height: 22px;
	}

	.slider-label {
		position: absolute;
		top: 0;
	}

	.linear-chart {
		overflow: hidden;
		border-radius: 12px;
	}

	.linear-chart div {
		height: 12px;
	}

	.linear-chart div:nth-of-type(1),
	.linear-chart div:nth-of-type(3) {
		background-color: #303235;
	}

	.linear-chart div:nth-of-type(2) {
		background-color: #2491ff;
	}

	.liquid-diagram-container,
	.occupancy-diagram-container,
	.garage-diagram-container {
		display: flex;
		justify-content: end;
	}

	.liquid-diagram {
		position: relative;
		width: 203px;
		height: 242px;
	}

	.occupancy-diagram {
		position: relative;
		width: 231px;
		height: 268px;
	}

	.garage-diagram {
		position: relative;
		width: 300px;
	}

	.liquid-label,
	.occupancy-label,
	.garage-label {
		pointer-events: none;
		position: absolute;
		font-size: 10px;
		color: #6e6e6e;
	}

	.garage-label:nth-of-type(1),
	.garage-label:nth-of-type(3),
	.occupancy-label:nth-of-type(2),
	.liquid-label:nth-of-type(2),
	.liquid-label:nth-of-type(3),
	.liquid-label:nth-of-type(4) {
		color: #fff;
	}

	.occupancy-label:nth-of-type(1) {
		top: 46%;
		left: -19%;
	}
	.occupancy-label:nth-of-type(2) {
		top: 74%;
		left: 8%;
	}

	.liquid-label:nth-of-type(1) {
		top: 46%;
		left: -27%;
	}
	.liquid-label:nth-of-type(2) {
		top: 31%;
		left: 14%;
	}
	.liquid-label.reverse:nth-of-type(2) {
		top: 31%;
		left: 19%;
	}
	.liquid-label:nth-of-type(3) {
		bottom: 1%;
		left: 19%;
	}
	.liquid-label.reverse:nth-of-type(3) {
		bottom: 1%;
		left: 14%;
	}
	.liquid-label:nth-of-type(4) {
		top: 58%;
		left: 13%;
	}

	.garage-label:nth-of-type(1) {
		top: 52%;
		left: 10%;
	}
	.garage-label:nth-of-type(2) {
		top: 11%;
		left: 80%;
	}
	.garage-label:nth-of-type(3) {
		top: 90%;
		left: 49%;
	}

	@media (max-width: 639.98px) {
		.liquid-diagram-container {
			margin-bottom: 24px;
			justify-content: center;
		}
		.occupancy-diagram-container,
		.garage-diagram-container {
			justify-content: center;
		}
	}
</style>
